import { useState } from "react"
import {
    Box,
    Checkbox,
    IconButton,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Typography,
    ListItemButton,
} from "@mui/material"
import { ListItemBox } from "lib/@components/ListItemBox"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { busyWhile } from "lib/@components/busy/busy-while"
import { confirm } from "lib/@dialogs/confirm"
import { successSnackbar } from "lib/snackbar/success-snackbar"
import { navigate } from "lib/routes/navigate"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import Iconify from "minimals-template/components/Iconify"
import { getCurrencySymbol, formatDate } from "../format-utils"
import { deleteScenario } from "routes/scenarios/controller/delete-scenario"
import { duplicateScenario } from "routes/scenarios/controller/duplicate-scenario"
import { RateCardCell } from "./rate-card-cell"
import { AnnualCostCell } from "./annual-cost-cell"
import { VariantsCountCell } from "./variants-count-cell"

export function ScenarioRow({ item, onAfterDelete }) {
    const [anchorEl, setAnchorEl] = useState(null)
    const { selectedIds, onToggleSelect, canManageOptimiser } = useBoundContext()
    const isSelected = selectedIds?.has(item.id)
    const facilityCount = item.facilitiesCount || 0
    const assetsCount = item.assetsCount || 0
    const currency = getCurrencySymbol(item.rateCardCurrency)
    const variants = item.variants || []
    const variantCount = item.variantCount || 1
    const rateCardCount = countDistinctRateCards()

    return (
        <ListItemButton
            data-cy="scenario-row"
            sx={{ px: 3, py: 1, borderBottom: 1, borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}
            onClick={navigateToScenario}
        >
            <ListItemBox sx={{ gap: 3 }}>
                <If value={canManageOptimiser}>
                    <Checkbox
                        size="small"
                        checked={isSelected || false}
                        onChange={handleToggleCheck}
                        onClick={stopProp}
                        sx={{ p: 0, mr: -1, flexShrink: 0 }}
                    />
                </If>
                <Box sx={{ flex: 3, minWidth: 200 }}>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: 16 }}>
                        {item.name}
                    </Typography>
                    <Typography color="text.secondary" sx={{ fontSize: "14px !important", lineHeight: 1.5 }}>
                        {facilityCount} {facilityCount === 1 ? "Facility" : "Facilities"} &middot;{" "}
                        {assetsCount.toLocaleString()} Assets
                    </Typography>
                </Box>
                <Box sx={{ flex: 2 }}>
                    <If value={item.regimeName}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                cursor: "pointer",
                                "&:hover .regime-name": { textDecoration: "underline" },
                            }}
                            onClick={goToRegime}
                        >
                            <Iconify
                                icon="heroicons:wrench-screwdriver"
                                width={20}
                                height={20}
                                sx={{ color: "common.black", flexShrink: 0 }}
                            />
                            <Typography
                                className="regime-name"
                                variant="body2"
                                color="text.primary"
                                noWrap
                                sx={{ fontSize: 16 }}
                            >
                                {item.regimeName}
                            </Typography>
                        </Box>
                        <Else>
                            <Typography variant="body2" color="text.secondary">
                                -
                            </Typography>
                        </Else>
                    </If>
                </Box>
                <Box sx={{ width: 120 }}>
                    <RateCardCell
                        name={item.rateCardName}
                        id={item.rateCardId}
                        count={rateCardCount}
                        variants={variants}
                    />
                </Box>
                <Box sx={{ width: 70 }}>
                    <VariantsCountCell count={variantCount} />
                </Box>
                <Box sx={{ width: 140, textAlign: "right" }}>
                    <AnnualCostCell cost={item.annualCost} currency={currency} />
                </Box>
                <Box sx={{ width: 120 }}>
                    <Typography variant="body2" color="text.secondary" noWrap>
                        {formatDate(item.created)}
                    </Typography>
                </Box>
                <If value={canManageOptimiser}>
                    <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
                        <IconButton aria-label="More options" size="small" onClick={openMenu}>
                            <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>
                            <MenuItem onClick={handleEdit}>
                                <ListItemIcon>
                                    <Iconify icon="solar:pen-bold" width={20} />
                                </ListItemIcon>
                                <ListItemText>Edit</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleDuplicate}>
                                <ListItemIcon>
                                    <Iconify icon="solar:copy-bold" width={20} />
                                </ListItemIcon>
                                <ListItemText>Duplicate</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handleDelete}>
                                <ListItemIcon>
                                    <Iconify icon="solar:trash-bin-trash-bold" width={20} color="error.main" />
                                </ListItemIcon>
                                <ListItemText sx={{ color: "error.main" }}>Delete</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                    <Else>
                        <Box sx={{ width: 40 }} />
                    </Else>
                </If>
            </ListItemBox>
        </ListItemButton>
    )

    function stopProp(e) {
        e.stopPropagation()
    }

    function handleToggleCheck(e) {
        e.stopPropagation()
        onToggleSelect?.(item.id)
    }

    function countDistinctRateCards() {
        const ids = new Set()
        if (item.rateCardId) ids.add(item.rateCardId)
        for (const v of variants) {
            if (v.rateCardId) ids.add(v.rateCardId)
        }
        return ids.size || 1
    }

    function openMenu(e) {
        e.stopPropagation()
        setAnchorEl(e.currentTarget)
    }

    function closeMenu(e) {
        if (e) e.stopPropagation()
        setAnchorEl(null)
    }

    function navigateToScenario() {
        navigate(`/app/optimiser/scenarios/${item.id}`)
    }

    function goToRegime(e) {
        e.stopPropagation()
        const groupId = item.regimeId?.replace(/^(live|ready)\|/, "")?.split(":")?.[0]
        if (!groupId) return
        navigate(`/app/schedules?id=${groupId}`)
    }

    function handleEdit(e) {
        e.stopPropagation()
        closeMenu()
        navigate(`/app/optimiser/scenarios/${item.id}/edit`)
    }

    async function handleDuplicate(e) {
        e.stopPropagation()
        closeMenu()
        const newScenario = await busyWhile(runDuplicate, "Duplicating scenario...")
        if (newScenario?.id) {
            successSnackbar("Scenario duplicated")
        }
    }

    function runDuplicate() {
        return duplicateScenario({ id: item.id })
    }

    async function handleDelete(e) {
        e.stopPropagation()
        closeMenu()
        const variantWarning =
            variantCount > 1 ? `\nThis scenario has ${variantCount} variants that will also be deleted.` : ""
        const ok = await confirm(
            `Move "${item.name}" to the Recycle Bin? You can restore it later.${variantWarning}`,
            "Delete Scenario"
        )
        if (!ok) return
        await busyWhile(() => deleteScenario({ id: item.id }), "Deleting scenario...")
        onAfterDelete?.()
        successSnackbar(`Scenario "${item.name}" moved to the Recycle Bin`)
    }
}
